import * as v from "valibot";

// 로그인/가입/비밀번호 재설정/이메일 인증 화면이 공유하는 입력 검증 스키마.
// 클라이언트(@app/ui 의 react-hook-form)와 서버(@hono/valibot-validator)가
// 같은 스키마를 쓰므로 규칙이 한 곳에만 존재한다.
//
// 이 파일은 서버 워커 번들에도 들어가므로 lingui 매크로를 절대 import 하지 않는다
// (shared/features/reminder/constants.ts 의 같은 제약 참고). 대신 각 검증의 메시지를
// 안정적인 "키"로 두고, 사람이 읽는 문구는 ./messages.ts 의 msg 디스크립터가 맡는다.

/** better-auth 의 기본 minPasswordLength 와 맞춘다. */
export const PASSWORD_MIN_LENGTH = 8;
/** shared/lib/auth.ts 의 emailOTP `otpLength` 와 맞춰야 한다. */
export const OTP_LENGTH = 6;

/** 검증 실패 메시지의 키. 실제 문구는 ./messages.ts 의 AUTH_MESSAGES 가 번역한다. */
export const AUTH_MESSAGE_KEYS = {
  emailRequired: "auth.email.required",
  emailInvalid: "auth.email.invalid",
  emailDisposable: "auth.email.disposable",
  nicknameRequired: "auth.nickname.required",
  passwordRequired: "auth.password.required",
  passwordTooShort: "auth.password.tooShort",
  passwordConfirmMismatch: "auth.passwordConfirm.mismatch",
  otpRequired: "auth.otp.required",
  otpLength: "auth.otp.length",
} as const;

export type AuthMessageKey = (typeof AUTH_MESSAGE_KEYS)[keyof typeof AUTH_MESSAGE_KEYS];

const K = AUTH_MESSAGE_KEYS;

export const emailSchema = v.pipe(
  v.string(K.emailRequired),
  v.trim(),
  v.nonEmpty(K.emailRequired),
  v.email(K.emailInvalid),
);

export const nicknameSchema = v.pipe(
  v.string(K.nicknameRequired),
  v.trim(),
  v.nonEmpty(K.nicknameRequired),
);

/** 새로 정하는 비밀번호(가입·재설정)에 쓰는 스키마. 최소 길이를 강제한다. */
export const newPasswordSchema = v.pipe(
  v.string(K.passwordRequired),
  v.nonEmpty(K.passwordRequired),
  v.minLength(PASSWORD_MIN_LENGTH, K.passwordTooShort),
);

/** 로그인 입력용. 기존 계정의 비밀번호를 길이로 되돌려 까지 않는다 — 비었는지만 본다. */
export const currentPasswordSchema = v.pipe(
  v.string(K.passwordRequired),
  v.nonEmpty(K.passwordRequired),
);

export const otpSchema = v.pipe(
  v.string(K.otpRequired),
  v.trim(),
  v.nonEmpty(K.otpRequired),
  v.length(OTP_LENGTH, K.otpLength),
);

/** 비밀번호 확인 값이 원본과 같은지. 에러는 확인 필드 위치로 보낸다(v.forward). */
const isPasswordConfirmMatched = <T extends { password: string; passwordConfirm: string }>(
  input: T,
) => input.password === input.passwordConfirm;

/** 로그인 폼 = better-auth signIn.email 입력. */
export const signInSchema = v.object({
  email: emailSchema,
  password: currentPasswordSchema,
});

/** POST /api/sign-up 의 요청 본문. 폼에만 있는 passwordConfirm 은 여기 없다. */
export const signUpSchema = v.object({
  email: emailSchema,
  nickname: nicknameSchema,
  password: newPasswordSchema,
  // 가입 시 브라우저에서 감지한 IANA 타임존. 없으면 서버가 비워 둔다.
  timezone: v.optional(v.pipe(v.string(), v.trim(), v.nonEmpty())),
});

/** 가입 폼 = signUpSchema + 비밀번호 확인(클라이언트 전용). */
export const signUpFormSchema = v.pipe(
  v.object({
    email: emailSchema,
    nickname: nicknameSchema,
    password: newPasswordSchema,
    passwordConfirm: v.string(),
  }),
  v.forward(
    v.check(isPasswordConfirmMatched, K.passwordConfirmMismatch),
    ["passwordConfirm"],
  ),
);

/** 비밀번호 찾기 폼. */
export const forgotPasswordSchema = v.object({
  email: emailSchema,
});

/** 비밀번호 재설정 폼. 토큰은 URL 에서 오므로 폼에 없다. */
export const resetPasswordFormSchema = v.pipe(
  v.object({
    password: newPasswordSchema,
    passwordConfirm: v.string(),
  }),
  v.forward(
    v.check(isPasswordConfirmMatched, K.passwordConfirmMismatch),
    ["passwordConfirm"],
  ),
);

/** 이메일 인증 코드 입력 폼. */
export const verifyEmailSchema = v.object({
  otp: otpSchema,
});

/** POST /api/sessions/revoke 의 요청 본문. 해제할 세션을 session.id 로 지목한다 —
 *  token 이 아니라 id 를 받아, 서버가 소유권(userId)까지 확인한 뒤 token 을 조회해
 *  취소한다(다른 유저의 세션을 token 추측으로 지우지 못하게). */
export const revokeSessionSchema = v.object({
  sessionId: v.pipe(v.string(), v.nonEmpty()),
});

// *FormValues = 사용자가 실제로 타이핑하는 값(InferInput) — react-hook-form 의
// 필드 타입. *Dto = 검증·정규화(trim)를 통과한 값(InferOutput) — 제출 콜백이 받는다.
export type SignInFormValues = v.InferInput<typeof signInSchema>;
export type SignInDto = v.InferOutput<typeof signInSchema>;
export type SignUpDto = v.InferOutput<typeof signUpSchema>;
export type SignUpFormValues = v.InferInput<typeof signUpFormSchema>;
export type SignUpFormDto = v.InferOutput<typeof signUpFormSchema>;
export type ForgotPasswordFormValues = v.InferInput<typeof forgotPasswordSchema>;
export type ForgotPasswordDto = v.InferOutput<typeof forgotPasswordSchema>;
export type ResetPasswordFormValues = v.InferInput<typeof resetPasswordFormSchema>;
export type ResetPasswordFormDto = v.InferOutput<typeof resetPasswordFormSchema>;
export type VerifyEmailFormValues = v.InferInput<typeof verifyEmailSchema>;
export type VerifyEmailDto = v.InferOutput<typeof verifyEmailSchema>;
export type RevokeSessionDto = v.InferOutput<typeof revokeSessionSchema>;
